import React, { useEffect, useState } from "react";
import { Check, Github, Zap } from "lucide-react";

export const GithubIntegrationAnimation: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="w-full h-44 bg-slate-50 dark:bg-slate-900/50 rounded-xl relative overflow-hidden border border-slate-100 dark:border-slate-800 mb-6 flex items-center justify-center">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] dark:opacity-[0.07]">
          <div
            className="absolute top-[-10%] left-[-10%] w-[120%] h-[120%]"
            style={{
              backgroundImage:
                "radial-gradient(circle, #4f46e5 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />
        </div>
        {/* Gradients */}
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-white/5 to-white/10 dark:via-transparent dark:to-slate-900/20" />
      </div>

      <div className="relative flex items-center justify-between w-full max-w-[300px] z-10 px-4">
        {/* Taskplan Side */}
        <div className="flex flex-col items-center gap-2">
          <div
            className={`w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-xl shadow-indigo-500/20 transition-transform duration-500 ${activeStep === 0 || activeStep === 3 ? "scale-110" : "scale-100"}`}
          >
            <Zap size={28} />
          </div>
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            Taskplan
          </span>
        </div>

        {/* Connection Line Area */}
        <div className="flex-1 px-2 relative flex items-center justify-center h-20">
          <div className="h-0.5 w-full bg-slate-200 dark:bg-slate-800 relative rounded-full overflow-hidden">
            {/* Animated flow line */}
            <div
              className={`absolute h-full bg-indigo-500 transition-all duration-1000 ease-in-out ${
                activeStep === 1
                  ? "left-0 right-0"
                  : activeStep === 2
                    ? "left-full right-0"
                    : activeStep === 3
                      ? "left-0 right-0"
                      : "left-0 right-full"
              }`}
            />
          </div>

          {/* Moving Task Node */}
          <div
            className={`absolute w-12 h-12 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-lg flex items-center justify-center transition-all duration-1000 ease-in-out z-20
              ${activeStep === 0 ? "opacity-0 scale-50 translate-x-[-60px]" : ""}
              ${activeStep === 1 ? "opacity-100 scale-100 translate-x-[-60px]" : ""}
              ${activeStep === 2 ? "opacity-100 scale-100 translate-x-[60px] bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800" : ""}
              ${activeStep === 3 ? "opacity-0 scale-50 translate-x-[60px]" : ""}
            `}
          >
            {activeStep < 2 ? (
              <div className="space-y-1.5">
                <div className="w-5 h-1 bg-slate-200 dark:bg-slate-700 rounded-full" />
                <div className="w-4 h-1 bg-slate-200 dark:bg-slate-700 rounded-full opacity-60" />
              </div>
            ) : (
              <div className="relative">
                <Zap
                  size={20}
                  className="text-indigo-600 dark:text-indigo-400 fill-indigo-600/20"
                />
                <div className="absolute inset-0 bg-indigo-400 blur-md opacity-40 animate-pulse" />
              </div>
            )}
          </div>
        </div>

        {/* GitHub Side */}
        <div className="flex flex-col items-center gap-2">
          <div
            className={`w-14 h-14 rounded-2xl bg-slate-900 dark:bg-white flex items-center justify-center text-white dark:text-slate-900 shadow-xl transition-transform duration-500 ${activeStep === 2 ? "scale-110" : "scale-100"}`}
          >
            <Github size={28} />
          </div>
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            GitHub
          </span>
        </div>
      </div>

      {/* Status Overlay */}
      <div className="absolute bottom-4 inset-x-0 flex flex-col items-center gap-1 overflow-hidden h-6">
        <div
          className={`flex items-center gap-2 transition-all duration-500 ${activeStep === 1 ? "translate-y-0 opacity-100" : activeStep > 1 ? "-translate-y-8 opacity-0" : "translate-y-8 opacity-0"}`}
        >
          <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse" />
          <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
            Automating Task...
          </span>
        </div>
        <div
          className={`absolute flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 px-3 py-1 rounded-full text-[9px] font-bold border border-emerald-100 dark:border-emerald-800/50 transition-all duration-500 ${activeStep === 2 ? "translate-y-0 opacity-100" : activeStep === 3 ? "-translate-y-8 opacity-0" : "translate-y-8 opacity-0"}`}
        >
          <Check size={12} strokeWidth={3} />
          GITHUB ACTION TRIGGERED
        </div>
      </div>
    </div>
  );
};

export default GithubIntegrationAnimation;
